import { useEffect, useState } from "react";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
} from "../../../components/ui/select";
import { Label } from "../../../components/ui/label";
import { Input } from "../../../components/ui/input";
import { Checkbox } from "../../../components/ui/checkbox";
import { PencilIcon, Eye, EyeOff } from "lucide-react";
import apiClient from "../../../utils/apiClient";
import toast from "react-hot-toast";
import server from "../../../utils/serverConfig";

const statusOptions = [
  { value: "active", label: "active" },
  { value: "inactive", label: "inactive" },
  { value: "deactivated", label: "deactivated" },
  { value: "dummy", label: "dummy" },
];

export function EditDhanHQUserModal({ userDetails, fetchFyersUserList, forceOpen = false, onForceClose }) {
  const [open, setOpen] = useState(false);

  // Single state object for all input fields
  const [userData, setUserData] = useState({
    clientId: "",
    name: "",
    phone: "",
    totpKey: "",
    pin: "",
    redirectUrl: "",
    status: "",
    password: "",
    apiKey: "",
    apiSecret: "",
    proxyUser: "",
    proxyPassword: "",
    proxyHost: "",
    proxyPort: 0,
    proxyToggle: false,
    proxyStaticIp: "",
  });

  const [visibleFields, setVisibleFields] = useState({});

  const toggleVisibility = (field) => {
    setVisibleFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  // Fetch user details on component load
  const getDecryptedUserDetails = async () => {
    const data = {
      client_id: userDetails.client_id,
      broker: userDetails.broker,
    };

    const config = {
      method: "post",
      url: `${server.godseye.baseUrl}get-dhan-hq-user-by-id`,
      headers: {
        "Content-Type": "application/json",
      },
      data: JSON.stringify(data),
    };

    try {
      const response = await apiClient.request(config);

      // Populate the form with the fetched data
      setUserData({
        clientId: response.data.client_id || "",
        name: response.data.name || "",
        phone: response.data.phone || "",
        totpKey: response.data.totp_key || "",
        pin: response.data.pin || "",
        redirectUrl: response.data.redirect_url || "",
        status: response.data.status || "",
        password: response.data.password || "",
        apiKey: response.data.api_key || "",
        apiSecret: response.data.api_secret || "",
        proxyUser: response.data.proxy_user || "",
        proxyPassword: response.data.proxy_password || "",
        proxyHost: response.data.proxy_host || "",
        proxyPort: response.data.proxy_port || 0,
        proxyToggle: response.data.proxy_toggle || false,
        proxyStaticIp: response.data.proxy_static_ip || "",
      });

    } catch (error) {
      console.error("Failed to get user details:", error);
    }
  };

  // Common handleChange function
  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateUser = () => {
    const data = {
      client_id: userData.clientId,
      name: userData.name,
      phone: userData.phone,
      totp_key: userData.totpKey,
      pin: userData.pin,
      redirect_url: userData.redirectUrl,
      status: userData.status,
      password: userData.password,
      api_key: userData.apiKey,
      api_secret: userData.apiSecret,
      proxy_user: userData.proxyUser,
      proxy_password: userData.proxyPassword,
      proxy_host: userData.proxyHost,
      proxy_port: parseInt(userData.proxyPort),
      proxy_toggle: userData.proxyToggle,
      proxy_static_ip: userData.proxyStaticIp,
      broker: userDetails.broker, // "dhan_hq"
    };

    console.log("Updated Dhan HQ User Data", data);

    let config = {
      method: "put",
      url: `${server.godseye.baseUrl}edit-dhan-hq-user`,
      headers: {
        "Content-Type": "application/json",
      },
      data: JSON.stringify(data),
    };

    apiClient
      .request(config)
      .then((response) => {
        console.log("Server response:", response.data);
        toast.success("Dhan HQ user updated successfully!", {
          duration: 5000,
        });
      })
      .catch((error) => {
        console.error("Failed to update user", error);
        toast.error("Failed to update user", {
          duration: 5000,
        });
      })
      .finally(() => {
        setOpen(false);
        fetchFyersUserList();
      });
  };

  const handleOpenModal = () => {
    setOpen(true);
    getDecryptedUserDetails();
  };

  useEffect(() => {
    if (forceOpen) {
      handleOpenModal();
    }
  }, [forceOpen]);

  return (
    <Dialog open={open} onOpenChange={(val) => { setOpen(val); if (!val && onForceClose) onForceClose(); }}>
      <DialogTrigger asChild>
        <Button
          onClick={handleOpenModal}
          variant="outline"
          className="text-[12px] h-[25px] font-medium bg-zinc-800 text-white hover:bg-zinc-700"
        >
          <PencilIcon className="mr-2 w-3 h-3 text-gray-500" />
          Edit User
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[1025px] border-zinc-700">
        <DialogHeader>
          <DialogTitle className="text-white">
            Edit Dhan HQ User Details ({userDetails.broker})
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-4 gap-4 py-4 max-h-[60vh] overflow-y-auto custom-scroll pr-4">
          {Object.keys(userData)
            .filter((field) => !["status", "proxyToggle"].includes(field))
            .map((field) => (
              <div key={field} className="grid grid-cols-1 items-center gap-4">
                <Label className="text-white">
                  {field.replace(/([A-Z])/g, " $1")}
                </Label>
                <div className="relative">
                  <Input
                    className="text-white pr-10"
                    type={
                      (field.toLowerCase().includes("password") || 
                       field.toLowerCase().includes("secret") || 
                       field.toLowerCase().includes("key") ||
                       field.toLowerCase().includes("token") ||
                       field.toLowerCase().includes("pin")) && !visibleFields[field] 
                        ? "password" 
                        : "text"
                    }
                    placeholder={field.replace(/([A-Z])/g, " $1")}
                    name={field}
                    value={userData[field]}
                    onChange={handleChange}
                  />
                  {(field.toLowerCase().includes("password") || 
                    field.toLowerCase().includes("secret") || 
                    field.toLowerCase().includes("key") ||
                    field.toLowerCase().includes("token") ||
                    field.toLowerCase().includes("pin")) && (
                    <button
                      type="button"
                      onClick={() => toggleVisibility(field)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                    >
                      {visibleFields[field] ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}

          {/* Custom Status Dropdown */}
          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Status</Label>
            <Select
              value={userData.status}
              onValueChange={(val) =>
                setUserData((prev) => ({ ...prev, status: val }))
              }
            >
              <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
                <SelectValue placeholder="Select Status" />
              </SelectTrigger>
              <SelectContent className="border-zinc-700">
                <SelectGroup>
                  {statusOptions.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2 h-full pt-8">
            <Checkbox
              id="proxy-toggle"
              checked={userData.proxyToggle}
              onCheckedChange={(checked) =>
                setUserData((prev) => ({ ...prev, proxyToggle: checked }))
              }
            />
            <Label htmlFor="proxy-toggle" className="text-white cursor-pointer">
              Proxy Toggle
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="submit"
            className="text-[16px] font-normal bg-zinc-800 text-white hover:bg-zinc-700"
            onClick={handleUpdateUser}
          >
            Update User
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
